import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  CLEAR_LOCAL_STORAGE,
  SET_ACCOUNT,
  SET_EMAIL_FOR_REGISTRATION,
  SET_JWT_TOKEN,
  SET_REFRESH_TOKEN,
} from '@/store/slices/storage'
import {CLEAR_USER_DATA} from '@/store/slices'
import {CLEAR_WALLET} from '@/store/slices/wallet'
import {AxiosEmailRequestSignUpInterface} from '@/types/types'
import AuthService from '@/services/api/Auth'
import {getUser} from '@/store/actions/index'
import {AxiosError} from 'axios'
import {RootState} from '@/store'

let isRefreshing = false

export const refreshToken = createAsyncThunk(
  'storage/refreshToken',
  async (_, {dispatch, getState}) => {
    if (isRefreshing) {
      return false
    }

    try {
      isRefreshing = true
      const refreshToken = (getState() as RootState).storage.refreshToken
      if (!refreshToken) {
        dispatch(CLEAR_LOCAL_STORAGE())
        return false
      }

      const response = await AuthService.refresh(refreshToken)

      if (response.status === 200) {
        dispatch(SET_JWT_TOKEN(response.data.access))
        dispatch(SET_REFRESH_TOKEN(response.data.refresh))
        await dispatch(getUser())
        return true
      } else {
        dispatch(CLEAR_LOCAL_STORAGE())
        return false
      }
    } catch (e) {
      dispatch(CLEAR_LOCAL_STORAGE())
      return false
    } finally {
      isRefreshing = false
    }
  }
)

export const authEmail = createAsyncThunk(
  'storage/authLogin',
  async (
    {username, password}: {username: string; password: string},
    {dispatch, rejectWithValue}
  ) => {
    try {
      const response = await AuthService.auth({
        email: username.toLowerCase(),
        password,
      })
      if (response.status === 200) {
        dispatch(CLEAR_USER_DATA())
        dispatch(CLEAR_WALLET())
        dispatch(SET_JWT_TOKEN(response.data.access))
        dispatch(SET_REFRESH_TOKEN(response.data.refresh))
        dispatch(SET_ACCOUNT(username.toLowerCase()))
        await dispatch(getUser())
        return true
      }
      return true
    } catch (e) {
      const typedError = e as AxiosError
      if (typedError.isAxiosError) {
        return rejectWithValue(
          typedError.response?.data || 'Unknown Axios error occurred'
        )
      }
    }
  }
)

export const signUpEmail = createAsyncThunk(
  'storage/signUpEmail',
  async (
    {
      name,
      language,
      email,
      password,
      referral_code,
    }: AxiosEmailRequestSignUpInterface,
    {dispatch, rejectWithValue}
  ) => {
    try {
      const data: AxiosEmailRequestSignUpInterface = {
        name,
        password,
        email: email.toLowerCase(),
        language,
        is_loi: false,
      }
      if (referral_code !== '') {
        data.referral_code = referral_code
      }
      const response = await AuthService.signUp(data)
      if (response.status === 200) {
        dispatch(SET_EMAIL_FOR_REGISTRATION(email.toLowerCase()))
      }
      return false
    } catch (e) {
      const typedError = e as AxiosError
      if (typedError.isAxiosError) {
        return rejectWithValue(
          typedError.response?.data || 'Unknown Axios error occurred'
        )
      }
    }
  }
)
